'use client';

import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useRef } from 'react';

import { UnauthQueryOptions } from '@/api';

import { springBounce, staggerCard, staggerContainer } from './animations';

const blinkAnimation = {
  animate: {
    color: ['#FFFFFF', '#E4302C', '#FFFFFF'],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

type Benefit = {
  number: string;
  content: React.ReactNode;
  subtext?: string;
};

const getSecondBenefits = (eventCount: number): Benefit[] => [
  {
    number: '기본',
    content: (
      <>
        <span className="text-[#e4302c]">10만원까지</span> 전액 기부금 세액공제
      </>
    ),
    subtext: '10만원 초과시, 초과금은 16.5% 공제',
  },
  {
    number: '기본',
    content: (
      <>
        <span className="text-[#e4302c]">3만원 상당</span> 프리미엄 답례품
      </>
    ),
    subtext: '20개 지역의 상위 1% 특산품',
  },
  {
    number: '특별\n기획',
    content: (
      <>
        {eventCount}개 지역별 <span className="text-[#e4302c]">12월 한정</span>{' '}
        이벤트
      </>
    ),
    subtext: '각 지역별 특별 혜택 이벤트',
  },
  {
    number: '01',
    content: (
      <>
        최대 <span className="text-[#e4302c]">1만원</span> 웰포인트 즉시 지급
      </>
    ),
    subtext: '24시간 동안, 매 시간 정각마다 선착순 오픈',
  },
  {
    number: '02',
    content: (
      <>
        최대 <span className="text-[#e4302c]">10만원</span> 웰포인트 랜덤 지급
      </>
    ),
    subtext: "'오늘의 지역X답례품' 기부 시",
  },
  {
    number: '03',
    content: (
      <>
        <span className="text-[#e4302c]">무제한</span> 웰포인트 +{' '}
        <span className="text-[#e4302c]">연말 선물</span> 추첨
      </>
    ),
    subtext: '이벤트 기간 내 친구 초대시',
  },
  {
    number: '04',
    content: (
      <>
        최대 <span className="text-[#e4302c]">100만원</span>의 경품 추첨
      </>
    ),
    subtext: '25년 12월 내 2회 이상 기부시',
  },
  {
    number: '05',
    content: (
      <>
        최대 <span className="text-[#e4302c]">1만원</span> 웰포인트 추가 지급
      </>
    ),
    subtext: '블로그/인스타그램에 후기 공유 시',
  },
  {
    number: '06',
    content: (
      <>
        <span className="text-[#e4302c]">1천원</span> 웰포인트 추가 지급
      </>
    ),
    subtext: '포토후기 작성 시',
  },
];

export const BasicBenefitSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const shouldReduceMotion = useReducedMotion();

  const { data } = useSuspenseQuery(
    UnauthQueryOptions['get:/wello/v2/allows/home/institution-event'](),
  );

  const eventCount = (data?.context?.contents ?? []).filter(
    (event) => !event.endedYN && !event.title?.includes('_제휴'),
  ).length;

  const benefits = getSecondBenefits(eventCount);

  const content = (
    <>
      <div className="flex flex-col items-center gap-4 text-center">
        <h2 className="flex gap-8 font-800 typo-h2">
          <motion.span
            animate={blinkAnimation.animate}
            className="inline-block"
          >
            MAX
          </motion.span>
          <span className="text-Gray-white">REWARD</span>
        </h2>
        <p className="font-700 text-Gray-white typo-h7">
          오직, 12월 &apos;웰로&apos; 한정{' '}
          <span className="font-700 text-[#e4302c]">최대 혜택 7가지</span>
        </p>
      </div>

      <div className="flex w-full max-w-[375px] flex-col">
        {benefits.map((benefit, index) => (
          <div
            key={index}
            className={cn(
              'flex w-full items-center border-b border-[#C3C3C3]/40 px-16 py-24',
              index === 0 && 'border-t',
            )}
          >
            <span className="w-40 font-600 text-Gray-white typo-body3">
              {benefit.number}
            </span>
            <div className="flex flex-1 flex-col items-end text-end">
              <p className="font-600 text-Gray-white typo-body2">
                {benefit.content}
              </p>
              {benefit.subtext && (
                <p className="font-400 text-Gray-600 typo-body4">
                  {benefit.subtext}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  if (shouldReduceMotion) {
    return (
      <section className="flex w-full flex-col items-center gap-30 bg-Gray-black px-24 py-60">
        {content}
      </section>
    );
  }

  return (
    <section className="flex w-full flex-col items-center gap-40 bg-Gray-black px-24 py-60">
      <motion.div
        ref={ref}
        variants={staggerContainer}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        className="flex w-full flex-col items-center gap-30"
      >
        <motion.div
          variants={springBounce}
          className="flex flex-col items-center gap-4 text-center"
        >
          <h2 className="font-800 typo-h2">
            <motion.span
              animate={blinkAnimation.animate}
              className="inline-block"
            >
              MAX
            </motion.span>{' '}
            <span className="text-Gray-white">REWARD</span>
          </h2>
          <p className="font-700 text-Gray-white typo-h7">
            오직, 12월 &apos;웰로&apos; 한정{' '}
            <span className="font-700 text-[#e4302c]">최대 혜택 7가지</span>
          </p>
        </motion.div>

        <motion.div
          variants={springBounce}
          className="flex w-full max-w-[375px] flex-col"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={staggerCard}
              className={cn(
                'flex w-full items-center border-b border-[#C3C3C3]/40 px-16 py-24',
                index === 0 && 'border-t',
              )}
            >
              <span className="w-40 font-600 text-Gray-white typo-body3">
                {benefit.number}
              </span>
              <div className="flex flex-1 flex-col items-end text-end">
                <p className="font-600 text-Gray-white typo-body2">
                  {benefit.content}
                </p>
                {benefit.subtext && (
                  <p className="font-400 text-Gray-600 typo-body4">
                    {benefit.subtext}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
